import React, { useState, useRef } from 'react';
import { useProjectsPaginated } from '@hooks/api/projects';
import { ProjectFilters as ProjectFiltersType } from '@models/project.model';
import ProjectFilters from '@components/organisms/ProjectFilters';
import ProjectPagination from '@components/molecules/ProjectPagination';
import { ProjectStatusBadge } from '@components/atoms/ProjectStatusBadge';
import { Event } from '@models/event.model';
import { Task } from '@models/task.model';
import { useTask } from '@hooks/api/task/useTask';
import { useTasks } from '@hooks/api/task/useTasks';
import { useEvents, useEvent } from '@hooks/api';
import { Timeline } from './TimeLine';
import { Navigation } from './Accordion/components/Navigation';
import { useTranslation } from '@hooks/useTranslation';
import { ChevronDownIcon } from 'lucide-react';
import { CreateEditTaskModal } from '@components/CreateEditTaskModal';
import { CreateEditEventModal } from '@components/CreateEditTaskModal/components/CreateEditEventModal';
import { TaskDetailModal } from '@components/organisms/TaskDetailModal';
import { Loading } from '@components/atoms/Loading';

const ProjectsList: React.FC = () => {
  const tSchedule = useTranslation('schedule');
  const viewLabels = {
    project: tSchedule.viewProject,
    month: tSchedule.viewMonth,
    monthTooltip: tSchedule.viewMonthTooltip,
    group: tSchedule.viewRangeLabel,
  };
  const [filters, setFilters] = useState<ProjectFiltersType>({
    page: 1,
    limit: 10,
    sortBy: 'name',
  });

  // Accordion expansion state
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [events] = useState<Event[]>([]);
  const [showNewTaskModal, setShowNewTaskModal] = useState<boolean>(false);
  const [showTaskDetailModal, setShowTaskDetailModal] =
    useState<boolean>(false);
  const [showNewSubtaskModal, setShowNewSubtaskModal] =
    useState<boolean>(false);
  const [subtaskParentTask, setSubtaskParentTask] = useState<Task | null>(null);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [projectId, setProjectId] = useState<string | null>(null);
  const timelineRefs = useRef<{ [key: string]: any }>({});

  // hooks for accordion functionality
  const { task, fetchTask, setTask } = useTask();
  const { tasks, fetchTasks } = useTasks(projectId || undefined);
  const { fetchEvents } = useEvents();
  const { event, fetchEvent, setEvent } = useEvent();

  const {
    data: paginatedData,
    isLoading,
    error,
    isRefetching,
  } = useProjectsPaginated(filters);

  const projects = paginatedData?.projects || [];
  const pagination = paginatedData?.pagination;

  const handleFiltersChange = (newFilters: ProjectFiltersType) => {
    setFilters({ ...newFilters, page: 1 }); // Reset to first page when filters change
  };

  const handleClearFilters = () => {
    setFilters({
      page: 1,
      limit: filters.limit,
      sortBy: 'name',
    });
  };

  const handlePageChange = (page: number) => {
    setFilters({ ...filters, page });
  };

  const handleLimitChange = (limit: number) => {
    setFilters({ ...filters, limit, page: 1 });
  };

  // Toggle accordion expansion
  const toggleAccordion = (projectId: string) => {
    const newExpandedItems = new Set(expandedItems);
    if (newExpandedItems.has(projectId)) {
      newExpandedItems.delete(projectId);
      setProjectId(null); // Clear current project when closing
    } else {
      newExpandedItems.add(projectId);
      setProjectId(projectId);
      // Fetch tasks and events for this project
      fetchTasks(projectId);
      fetchEvents(projectId);
    }
    setExpandedItems(newExpandedItems);
  };

  // Task and event handlers
  const toggleNewTaskModal = (projectId: string) => {
    setProjectId(projectId);
    setShowNewTaskModal(true);
    setTask(null);
    setEvent(null);
  };

  const toggleNewEventModal = (projectId: string) => {
    setProjectId(projectId);
    setIsEventModalOpen(true);
    setEvent(null);
  };

  const handleSetTask = (id: string) => {
    fetchTask(id);
    setShowNewTaskModal(true);
  };

  const handleViewTaskDetails = (id: string) => {
    fetchTask(id);
    setShowTaskDetailModal(true);
  };

  const handleSetEvent = (id: string) => {
    fetchEvent(id);
    setIsEventModalOpen(true);
  };

  const handleEventSuccess = () => {
    setIsEventModalOpen(false);
    setEvent(null);
    if (projectId) {
      fetchEvents(projectId);
      fetchTasks(projectId);
    }
  };

  const successTask = () => {
    if (projectId) {
      fetchTasks(projectId);
    }
    if (task?.taskId) {
      fetchTask(task.taskId);
    }
    setShowNewTaskModal(false);
  };

  const successSubtask = () => {
    if (projectId) {
      fetchTasks(projectId);
    }
    if (task?.taskId) {
      fetchTask(task.taskId);
    }
    setShowNewSubtaskModal(false);
    setSubtaskParentTask(null);
  };

  const handleCreateSubtask = (parentTask: Task) => {
    setSubtaskParentTask(parentTask);
    setShowNewSubtaskModal(true);
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Error loading projects
          </h3>
          <p className="text-sm text-gray-500">
            {error instanceof Error ? error.message : 'An error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <ProjectFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClear={handleClearFilters}
      />

      {isLoading && !isRefetching ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loading center size="xl" />
          </div>
        </div>
      ) : projects.length === 0 ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No projects found
            </h3>
            <p className="text-sm text-gray-500">
              Try adjusting your filters to see more results.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="">
            <ul className="divide-y divide-gray-200">
              {projects.map((project) => {
                const isExpanded = expandedItems.has(project.projectId!);
                return (
                  <li
                    key={project.projectId}
                    className="rounded-lg overflow-hidden mb-4 last:mb-0 shadow-md">
                    {/* Project Header */}
                    <div className="cursor-pointer">
                      <div
                        className="flex items-center justify-between w-full p-4 font-medium text-left text-gray-700 bg-gray-100 hover:bg-gray-200 focus:ring-4 focus:ring-blue-200 transition-colors duration-200"
                        onClick={() => toggleAccordion(project.projectId!)}>
                        <div className="flex flex-col items-start gap-1 flex-1">
                          <div className="flex items-center justify-between mr-10 w-full">
                            <span className="text-base font-semibold text-blue-800 capitalize">
                              {project.originalProjectId &&
                              project.originalProject?.organization?.name
                                ? `${project.name} (from: ${project.originalProject.organization.name})`
                                : project.name}
                            </span>
                            <div className="flex items-center space-x-3">
                              <ProjectStatusBadge
                                state={project.state}
                                isBehind={project.isBehind}
                                size="sm"
                              />
                            </div>
                          </div>
                          <span className="text-sm font-normal text-gray-600">
                            Address: {project.address}
                          </span>
                        </div>
                        <ChevronDownIcon
                          className={`transition-transform duration-200 ml-4 ${
                            isExpanded ? 'rotate-180' : ''
                          }`}
                        />
                      </div>
                    </div>

                    {/* Accordion Content */}
                    {isExpanded && (
                      <div className="transition-all duration-200 ease-in-out">
                        <div className="p-0 m-0 min-h-0 overflow-visible">
                          {/* Navigation Component */}
                          <Navigation
                            projectId={project.projectId!}
                            timelineRefs={timelineRefs}
                            toggleNewTaskModal={toggleNewTaskModal}
                            toggleNewEventModal={toggleNewEventModal}
                            isDelegated={
                              project.originalProjectId ? true : false
                            }
                            viewLabels={viewLabels}
                          />

                          {/* Timeline Component */}
                          <Timeline
                            ref={(ref: any) => {
                              if (ref) {
                                timelineRefs.current[project.projectId!] = ref;
                              }
                            }}
                            flag={false}
                            events={events}
                            tasks={tasks}
                            setTask={handleSetTask}
                            setTaskDetails={handleViewTaskDetails}
                            setEvent={handleSetEvent}
                            projectId={project.projectId!}
                            projectStartDate={project.startDate}
                            projectEndDate={project.endDate}
                            isDelegated={
                              project.originalProjectId ? true : false
                            }
                          />
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>

          {pagination && (
            <ProjectPagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </>
      )}

      {isRefetching && (
        <div className="fixed top-4 right-4 bg-blue-600 text-white px-4 py-2 rounded-md shadow-lg">
          <div className="flex items-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4"></div>
            <span>Updating...</span>
          </div>
        </div>
      )}

      {/* Task Modal - stays open when subtask modal opens on top */}
      <CreateEditTaskModal
        mode="task"
        task={task}
        tasks={tasks}
        events={events}
        project={projects.find((p) => p.projectId === projectId) || null}
        successTask={successTask}
        isOpen={showNewTaskModal}
        projectId={projectId || undefined}
        onCreateSubtaskClick={(parentTask) => handleCreateSubtask(parentTask)}
        onClose={() => {
          setShowNewTaskModal(false);
          setTask(null);
        }}
      />

      {/* Subtask Modal - opens on top of task modal (narrower) */}
      <CreateEditTaskModal
        mode="subtask"
        task={subtaskParentTask}
        tasks={tasks}
        events={events}
        project={projects.find((p) => p.projectId === projectId) || null}
        successTask={successSubtask}
        isOpen={showNewSubtaskModal}
        projectId={projectId || undefined}
        onClose={() => {
          setShowNewSubtaskModal(false);
          setSubtaskParentTask(null);
        }}
      />

      <CreateEditEventModal
        event={event}
        tasks={tasks}
        projectId={projectId!}
        isOpen={isEventModalOpen}
        onSuccess={handleEventSuccess}
        onClose={() => setIsEventModalOpen(false)}
      />

      {task && projects.find((p) => p.projectId === projectId) && (
        <TaskDetailModal
          task={task}
          project={projects.find((p) => p.projectId === projectId)!}
          isTaskDetailModal={showTaskDetailModal}
          setIsTaskDetailModal={setShowTaskDetailModal}
        />
      )}
    </div>
  );
};

export default ProjectsList;
